import { useTranslation } from 'react-i18next';
import { useSensors } from '../../hooks/useSensors';
import SensorMap from '../Soil/SensorMap';
import ParameterCard from '../Shared/ParameterCard';
import { useState, useMemo } from 'react';
import { useSensorHistory, toSeries } from '../../hooks/useSensorHistory';
import { Sensor } from '../../services/sensors';
import SensorModal from '../Soil/SensorModal';
import { DEPTHS } from '../../data/demo';
import { useSensorNames } from '../../context/SensorNamesContext';
import { markerLabel } from '../../utils/deviceMarker';
import WeatherMap from '../Weather/WeatherMap';
import { useUbicacionClima } from '../../hooks/useUbicacionClima';
import { useClima } from '../../hooks/useClima';
import { condicionDe } from '../../services/weather';
import { useSettings } from '../../context/SettingsContext';

/**
 * Valor del sensor a la profundidad MÁS SOMERA que realmente tenga dato.
 * Antes se leía `metrics.humidity_5` fijo, y cualquier sonda que no midiera
 * justo a 5 cm mostraba "0.0" con badge Warning — el caso de los sensores
 * Xiaoma, cuyas profundidades salen del portMap y rara vez incluyen 5 cm.
 * Devuelve null si no hay dato a ninguna profundidad; nunca 0.
 */
/**
 * Tarjetas de un sensor con su miniatura de tendencia REAL.
 *
 * Antes las dos gráficas recibían un arreglo escrito a mano —
 * `[35, 38, 42, 40, 45]` para humedad y `[22, 23, 22, 24, 23]` para
 * temperatura — así que TODOS los sensores de TODOS los clientes dibujaban
 * la misma curva, no cambiaba nunca, y no tenía relación con el número de
 * arriba. El tipo de la prop decía "últimos 20 puntos": era un marcador de
 * posición que se quedó.
 *
 * Ahora sale de las últimas 24 h reales del sensor. Sin historia suficiente
 * no se dibuja nada, que es el mismo criterio del resto de la plataforma.
 */
function SensorCriticalParams({ sensor, onSelect }: { sensor: Sensor; onSelect: () => void }) {
  const { t } = useTranslation();
  const { getName } = useSensorNames();
  const { readings } = useSensorHistory(sensor.imei, { hours: 24 });
  // Mismo nombre que muestran Suelo, Batería y Locations. Sin esto la tarjeta
  // encabezaba con el IMEI crudo mientras el resto de la plataforma decía
  // "Santa Maria #1", como si fueran dos sensores distintos.
  const nombre = getName(sensor.imei, sensor.name);

  const humedad = useMemo(() => toSeries(readings, 'humidity').map(p => p.value), [readings]);
  const temp = useMemo(() => toSeries(readings, 'temp').map(p => p.value), [readings]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      <ParameterCard
        name={`${nombre} — ${t('home.humidity')}`}
        paramKey="humidity"
        value={shallowestValue(sensor.lastReading?.metrics, 'humidity') ?? 0}
        unit="%"
        lo={20} hi={60}
        history={humedad}
        onClick={onSelect}
      />
      <ParameterCard
        name={`${nombre} — ${t('home.temp')}`}
        paramKey="temp"
        value={shallowestValue(sensor.lastReading?.metrics, 'temp') ?? 0}
        unit="°C"
        lo={15} hi={35}
        history={temp}
        onClick={onSelect}
      />
    </div>
  );
}

function shallowestValue(metrics: Record<string, number> | undefined, param: string): number | null {
  if (!metrics) return null;
  for (const d of DEPTHS) {
    const v = metrics[`${param}_${d}`];
    if (typeof v === 'number' && Number.isFinite(v)) return v;
  }
  return null;
}

export default function HomePage() {
  const { t } = useTranslation();
  const { sensors, loading } = useSensors();

  /**
   * Punto con el que se consulta el clima.
   *
   * Sale de Configuración si el cliente ya lo eligió; si no, del promedio de
   * sus sensores. Ver useUbicacionClima.ts para por qué en ese orden.
   */
  const { ubicacion: ubicacionClima, esDeSensores } = useUbicacionClima(sensors);
  const { clima } = useClima(ubicacionClima?.lat ?? null, ubicacionClima?.lng ?? null);
  const { formatTemp } = useSettings();
  const { getName } = useSensorNames();
  const [selectedSensor, setSelectedSensor] = useState<Sensor | null>(null);

  const activeSensors = sensors.filter(s => s.status === 'good').length;
  const alertSensors = sensors.filter(s => s.status === 'alert').length;
  // Los que ya mandaron al menos una lectura. Un device recién dado de alta
  // sale en el mapa (para confirmar que quedó bien y dónde está) pero no en
  // las tarjetas de valores, porque no tiene ningún valor que mostrar.
  const reporting = sensors.filter(s => s.lastReading);

  // Antes esto era el literal "42.8" hardcodeado en el JSX: se mostraba igual
  // con cero sensores, así que un cliente real sin datos veía un número
  // inventado con pinta de medición. Ahora se promedian las lecturas reales de
  // humedad (las claves vienen como humidity_<profundidad> desde sensor-view)
  // y si no hay ninguna se muestra un guion.
  const humidityValues = sensors.flatMap(s =>
    Object.entries(s.lastReading?.metrics ?? {})
      .filter(([k, v]) => k.startsWith('humidity_') && typeof v === 'number')
      .map(([, v]) => v as number)
  );
  const avgHumidity = humidityValues.length > 0
    ? (humidityValues.reduce((a, b) => a + b, 0) / humidityValues.length).toFixed(1)
    : null;

  // La burbuja lleva el tipo (SS/WS/PS), igual que en Suelo, y el nombre va
  // en el tooltip. Antes llevaba `s.name`, que para un device sin etiqueta en
  // la base es el IMEI: quince dígitos desbordados sobre el mapa.
  const markers = sensors.map(s => ({
    id: s.id,
    label: markerLabel(s.type),
    title: getName(s.imei, s.name),
    lat: Number(s.lat),
    lng: Number(s.lng),
    status: s.status as 'good' | 'warn' | 'alert' | 'offline'
  }));

  // El centro por defecto son las coordenadas de la finca demo (Georgia). Con
  // sensores reales hay que ir a donde están de verdad, si no un cliente de
  // México abre su panel y ve campos de Georgia.
  const mapCenter: [number, number] = markers.length > 0
    ? [
        markers.reduce((a, m) => a + m.lat, 0) / markers.length,
        markers.reduce((a, m) => a + m.lng, 0) / markers.length,
      ]
    : [32.5756, -83.8675];

  return (
    <div className="page" style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '24px' }}>
      <header>
        <h1 style={{ fontSize: '24px', fontWeight: 600, color: 'var(--dark)' }}>{t('home.title')}</h1>
        <p style={{ color: 'var(--gray)', fontSize: '14px' }}>{t('home.subtitle')}</p>
      </header>

      {/* KPI Row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '20px' }}>
        <KPICard label={t('home.kpi.sensorsOnline')}    value={`${activeSensors}/${sensors.length}`} status="good" />
        <KPICard label={t('home.kpi.soilHumidityAvg')}  value={avgHumidity ?? '—'} unit={avgHumidity ? '%' : ''} status="good" />
        <KPICard label={t('home.kpi.systemAlerts')}     value={alertSensors.toString()} status={alertSensors > 0 ? 'alert' : 'good'} />
        {/* Ya hay fuente de clima real (módulo weather), así que esto dejó
            de ser adorno de demo: sale de la ubicación que el cliente eligió
            en Configuración. Sigue mostrando "—" cuando no hay ubicación o
            el proveedor no contestó — antes que inventar un "Soleado". */}
        <KPICard
          label={t('home.kpi.weatherStatus')}
          value={
            clima?.current?.temperature_2m !== undefined
              ? `${condicionDe(clima.current.weather_code).icono} ${formatTemp(clima.current.temperature_2m)}`
              : '—'
          }
          status="good"
        />
      </div>

      {/* Alto fijo y no `flex: 1` con mínimo de 400. Con el mapa del clima
          abajo, el de sensores ya no tiene que llenar la pantalla él solo:
          crecía hasta empujar todo lo demás fuera de la vista. 280 alcanza
          de sobra para ubicar los equipos de un vistazo, que es su trabajo
          aquí — el detalle se ve en Soil o en Locations. Va como MÍNIMO y
          no como alto fijo: si las tarjetas de al lado necesitan más, la
          fila crece en vez de recortarlas. */}
      <div style={{ display: 'flex', gap: '24px', height: '340px' }}>
        {/* Left: Map Summary */}
        <div style={{ flex: 2, background: 'var(--bg)', borderRadius: '16px', border: '1px solid var(--border)', overflow: 'hidden', position: 'relative' }}>
          <div className="glass" style={{ position: 'absolute', top: '16px', left: '16px', zIndex: 1000, background: 'rgba(255,255,255,0.9)', padding: '8px 12px', borderRadius: '8px', fontSize: '12px', fontWeight: 600, border: '1px solid var(--border)' }}>
            {t('home.liveMap')}
          </div>
          <SensorMap
            center={mapCenter}
            zoom={14}
            markers={markers}
            onMarkerClick={id => setSelectedSensor(sensors.find(s => s.id === id) ?? null)}
          />
        </div>

        {/* Right: Critical Parameters */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '16px', overflowY: 'auto', minHeight: 0 }}>
          <h2 style={{ fontSize: '16px', fontWeight: 600 }}>{t('home.criticalParams')}</h2>
          {/* Solo sensores que YA reportaron: el `|| 0` de antes pintaba
              "0.0 %" con badge de Warning para un device recién registrado —
              indistinguible de una medición real de cero. Los que todavía no
              reportan igual se ven en el mapa, en gris. */}
          {reporting.slice(0, 2).map(sensor => (
            <SensorCriticalParams
              key={sensor.id}
              sensor={sensor}
              onSelect={() => setSelectedSensor(sensor)}
            />
          ))}
          {loading && <p style={{ color: 'var(--gray)', fontSize: '13px' }}>{t('home.syncing')}</p>}
          {!loading && reporting.length === 0 && (
            <p style={{ color: 'var(--gray)', fontSize: '13px', lineHeight: 1.6 }}>
              {sensors.length > 0 ? t('home.awaitingFirstReading', { count: sensors.length }) : t('home.noDevices')}
            </p>
          )}
        </div>
      </div>

      {/* ── Mapa del clima ──────────────────────────────────────────
          Aparte del de sensores a propósito. El de arriba contesta "cómo
          van mis equipos"; éste contesta "qué tiempo viene". Mezclarlos
          hacía que la lluvia animada tapara justo lo que se quería leer. */}
      <div style={{ marginTop: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: '12px' }}>
          <h2 style={{ fontSize: '16px', fontWeight: 600 }}>{t('weather.mapTitle')}</h2>
          {ubicacionClima && esDeSensores && (
            <span style={{ fontSize: '11px', color: 'var(--gray)' }}>{t('weather.usingSensorAverage')}</span>
          )}
        </div>
        <div style={{ height: '360px', borderRadius: '16px', overflow: 'hidden', border: '1px solid var(--border)' }}>
          {ubicacionClima ? (
            <WeatherMap
              lat={ubicacionClima.lat}
              lng={ubicacionClima.lng}
              nombre={ubicacionClima.nombre || undefined}
            />
          ) : (
            <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 6, background: 'var(--bg)' }}>
              <span style={{ fontSize: '13px', color: 'var(--dark)', fontWeight: 600 }}>{t('weather.noLocation')}</span>
              <span style={{ fontSize: '12px', color: 'var(--gray)' }}>{t('weather.noLocationHint')}</span>
            </div>
          )}
        </div>
      </div>

      {selectedSensor && (
        <SensorModal sensor={selectedSensor} onClose={() => setSelectedSensor(null)} />
      )}
    </div>
  );
}

function KPICard({ label, value, unit = '', status }: { label: string; value: string; unit?: string; status: string }) {
  const color = status === 'alert' ? 'var(--alert)' : status === 'warn' ? 'var(--warn)' : 'var(--green-dk)';

  return (
    <div className="tile" style={{ background: 'var(--bg)', padding: '20px', borderRadius: '16px', border: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: '8px' }}>
      <span style={{ fontSize: '12px', color: 'var(--gray)', fontWeight: 500 }}>{label}</span>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px' }}>
        <span className="num" style={{ fontSize: '24px', fontWeight: 700, color: 'var(--dark)' }}>{value}</span>
        <span style={{ fontSize: '14px', color: 'var(--gray)' }}>{unit}</span>
      </div>
      <div style={{ height: '4px', width: '40px', background: color, borderRadius: '2px' }} />
    </div>
  );
}
